import { useState, useEffect, useCallback } from 'react'
import { FileText, User, Clock, Pause, Play } from 'lucide-react'
import { Button } from '@/components/common/Button'

interface BatchInfo {
  batchId: string
  sectionIndex: number
  totalSections: number
  sectionIdentifier?: string | null
  isContinueMode: boolean
  startedAt?: string
  resolvedSections?: number
}

interface BatchInfoHeaderProps {
  batchInfo: BatchInfo
  onPause?: () => void
  className?: string
}

function useTimeElapsed(startedAt?: string): string {
  const [elapsed, setElapsed] = useState('')

  useEffect(() => {
    if (!startedAt) {
      setElapsed('')
      return
    }

    const update = () => {
      const diff = Date.now() - new Date(startedAt).getTime()
      const minutes = Math.floor(diff / 60000)
      const hours = Math.floor(minutes / 60)

      if (hours > 0) {
        setElapsed(`vor ${hours}h ${minutes % 60}m`)
      } else if (minutes > 0) {
        setElapsed(`vor ${minutes}m`)
      } else {
        setElapsed('gerade')
      }
    }

    update()
    const interval = setInterval(update, 60000) // Update every minute
    return () => clearInterval(interval)
  }, [startedAt])

  return elapsed
}

export function BatchInfoHeader({ batchInfo, onPause, className }: BatchInfoHeaderProps) {
  // Safe values with defaults
  const safeSectionIndex = Math.max(0, batchInfo?.sectionIndex ?? 0)
  const safeTotalSections = Math.max(1, batchInfo?.totalSections ?? 1)
  const reportedDone = batchInfo?.resolvedSections ?? safeSectionIndex
  const done = Math.max(safeSectionIndex, Math.min(Math.max(0, reportedDone), safeTotalSections))
  const progressPercent = Math.round((done / safeTotalSections) * 100)
  const elapsed = useTimeElapsed(batchInfo?.startedAt)

  const handlePause = useCallback(() => {
    if (onPause) {
      onPause()
    }
  }, [onPause])

  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-2 px-4 py-3 bg-blue-50 border-b border-blue-100 ${className || ''}`}
    >
      {/* Left side: Page indicator and progress */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Page indicator */}
        <div className="flex items-center gap-1.5 text-sm">
          <FileText className="w-4 h-4 text-blue-600" />
          <span className="font-medium text-gray-700">
            Seite {safeSectionIndex + 1} von {safeTotalSections}
          </span>
        </div>

        {/* Progress bar */}
        <div className="flex items-center gap-2">
          <div className="h-2 w-24 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-600 transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <span className="text-xs text-gray-500">{progressPercent}%</span>
        </div>
      </div>

      {/* Middle: Section identifier and time */}
      <div className="flex items-center gap-4 flex-wrap">
        {/* Section identifier (teacher code) */}
        {batchInfo?.sectionIdentifier && (
          <div className="flex items-center gap-1.5 text-sm">
            <User className="w-4 h-4 text-gray-500" />
            <span className="text-gray-600">Lehrer:</span>
            <span className="font-medium text-gray-800">{batchInfo.sectionIdentifier}</span>
          </div>
        )}

        {/* Time elapsed */}
        {elapsed && (
          <div className="flex items-center gap-1.5 text-sm text-gray-500">
            <Clock className="w-4 h-4" />
            <span>{elapsed}</span>
          </div>
        )}
      </div>

      {/* Right side: Mode badge and pause button */}
      <div className="flex items-center gap-2">
        {/* Mode badge */}
        {batchInfo?.isContinueMode ? (
          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-800">
            <Play className="w-3 h-3" />
            Fortsetzen
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-green-100 text-green-800">
            <FileText className="w-3 h-3" />
            Neu
          </span>
        )}

        {/* Pause button */}
        {onPause && (
          <Button
            variant="secondary"
            size="sm"
            onClick={handlePause}
            className="flex items-center gap-1 text-xs"
          >
            <Pause className="w-3 h-3" />
            Pause
          </Button>
        )}
      </div>
    </div>
  )
}

export type { BatchInfo, BatchInfoHeaderProps }
